import type React from 'react'
import type { PropsWithChildren } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { formatPercentage } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

export const ComplianceItemContainer: React.FC<
  PropsWithChildren<{ isLast?: boolean; className?: string }>
> = ({ children, isLast, className }) => (
  <div
    className={cn(
      'flex flex-col py-2',
      !isLast && 'border-b border-primary-border',
      className,
    )}
  >
    {children}
  </div>
)

export const HeaderContainer: React.FC<
  PropsWithChildren<{ isReactNode?: boolean }>
> = ({ children, isReactNode }) => (
  <div
    className={cn('flex items-center justify-between', isReactNode && 'mb-2')}
  >
    {children}
  </div>
)

export const LabelContainer: React.FC<
  PropsWithChildren<{ hasViolations?: boolean }>
> = ({ children, hasViolations }) => (
  <div
    className={cn(
      'flex items-center justify-between gap-1',
      hasViolations && 'w-[60%]',
    )}
  >
    {children}
  </div>
)

export const ViolationsContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex items-center gap-2">{children}</div>

const ViolationIconContainer: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="flex items-center gap-1 text-moment-red-700">{children}</div>
)

export const ViolationsListContainer: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="mt-2 space-y-2 bg-moment-grey-100 dark:bg-moment-black-200 rounded-md p-2">
    {children}
  </div>
)

export const ViolationsHeaderContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex justify-between">{children}</div>

export const ViolationItemContainer: React.FC<
  PropsWithChildren<{ isLast?: boolean }>
> = ({ children, isLast }) => (
  <div
    className={cn(
      'flex flex-col',
      !isLast && 'border-b border-primary-border pb-2',
    )}
  >
    {children}
  </div>
)

interface ViolationDetailsProps {
  description?: string
  note?: string
  isin: string
  weight: number
  marketValue?: number
  onBondDetailsClick: (
    isin: string,
  ) => (e: React.MouseEvent<HTMLDivElement>) => void
  hidePopout?: boolean
  isMarketValueConstraint?: boolean
}

export const ViolationDetails: React.FC<ViolationDetailsProps> = ({
  description,
  note,
  isin,
  weight,
  marketValue,
  onBondDetailsClick,
  hidePopout = false,
  isMarketValueConstraint = false,
}) => (
  <div className="flex items-center justify-between">
    <div>
      <BodySubtext>{description || isin}</BodySubtext>
      {(description || note) && (
        <BodyXS className="text-secondary">
          {[description ? isin : undefined, note].filter(Boolean).join(' · ')}
        </BodyXS>
      )}
    </div>
    <div className="flex items-center gap-2">
      <BodySubtext>
        {isMarketValueConstraint && marketValue !== undefined
          ? `$${marketValue.toLocaleString()}`
          : formatPercentage(weight, { numDigits: 2 })}
      </BodySubtext>
      {!hidePopout && (
        // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onBondDetailsClick(isin)(
                e as unknown as React.MouseEvent<HTMLDivElement>,
              )
            }
          }}
          onClick={(e) => onBondDetailsClick(isin)(e)}
          className="cursor-pointer"
        >
          <Icon
            iconName={IconName.ArrowTopRightOnSquareIcon}
            size="small"
            className="text-secondary hover:text-primary transition-colors"
          />
        </div>
      )}
    </div>
  </div>
)

interface ViolationsButtonProps {
  violationCount: number
  isExpanded: boolean
  onClick: () => void
  shouldShowViolations?: boolean
}

const InteractiveViolationsButton: React.FC<
  Omit<ViolationsButtonProps, 'shouldShowViolations'>
> = ({ violationCount, isExpanded, onClick }) => (
  // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onClick()
      }
    }}
    className="cursor-pointer"
  >
    <BodyXS className="text-secondary">Violations</BodyXS>
    <ViolationIconContainer>
      <Icon iconName={IconName.ExclamationTriangleIcon} size="small" />
      <BodySubtext>{violationCount}</BodySubtext>
      <Icon
        iconName={IconName.ChevronDownIcon}
        size="small"
        className={cn(
          'transition-transform duration-200 text-secondary',
          isExpanded && 'transform rotate-180',
        )}
      />
    </ViolationIconContainer>
  </div>
)

const NonInteractiveViolationsButton: React.FC<
  Pick<ViolationsButtonProps, 'violationCount'>
> = ({ violationCount }) => (
  <div>
    <BodyXS className="text-secondary">Violations</BodyXS>
    <ViolationIconContainer>
      <Icon iconName={IconName.ExclamationTriangleIcon} size="small" />
      <BodySubtext>{violationCount}</BodySubtext>
    </ViolationIconContainer>
  </div>
)

export const ViolationsButton: React.FC<ViolationsButtonProps> = ({
  violationCount,
  isExpanded,
  onClick,
  shouldShowViolations = true,
}) =>
  shouldShowViolations ? (
    <InteractiveViolationsButton
      violationCount={violationCount}
      isExpanded={isExpanded}
      onClick={onClick}
    />
  ) : (
    <NonInteractiveViolationsButton violationCount={violationCount} />
  )
